import type OlMap from "ol/Map";
import { useCallback, useEffect, useRef, useState } from "react";

import { useNgwMapContext } from "@nextgis/react-ngw-map";

import { useTranslation } from "../i18n";
import type { SearchField, SearchItem } from "../types";
import {
  getSearchFieldKeys,
  getSearchLabel,
  zoomToSearchItem,
} from "../utils/search";

import "./SearchPanel.css";

export function SearchPanel({
  searchResourceId,
}: {
  searchResourceId: number;
}) {
  const { t } = useTranslation();
  const { ngwMap } = useNgwMapContext<OlMap>();
  const [query, setQuery] = useState("");
  const [fields, setFields] = useState<SearchField[]>([]);
  const [results, setResults] = useState<SearchItem[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "error" | "empty">(
    "idle",
  );
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;
    ngwMap.connector
      .getResource(searchResourceId, { cache: true })
      .then((resource) => {
        if (!cancelled) {
          setFields(resource?.feature_layer?.fields || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFields([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ngwMap, searchResourceId]);

  const runSearch = useCallback(
    async (value: string) => {
      const text = value.trim();
      abortRef.current?.abort();

      if (text.length < 2) {
        setResults([]);
        setStatus("idle");
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      setStatus("loading");

      try {
        const items = (await ngwMap.fetchNgwLayerItems({
          resourceId: searchResourceId,
          limit: 8,
          geom: false,
          fields: getSearchFieldKeys(fields),
          ilike: `%${text}%`,
          signal: controller.signal,
        })) as SearchItem[];
        setResults(items);
        setStatus(items.length ? "idle" : "empty");
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error(error);
        setStatus("error");
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [fields, ngwMap, searchResourceId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => runSearch(query), 250);
    return () => window.clearTimeout(timer);
  }, [query, runSearch]);

  return (
    <form
      autoComplete="off"
      onSubmit={(event) => {
        event.preventDefault();
        runSearch(query);
      }}
    >
      <label htmlFor="search-input">{t("search.label")}</label>
      <div className="search-row">
        <input
          id="search-input"
          type="search"
          placeholder={t("search.placeholder")}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
      </div>
      <div className="search-results">
        {status === "loading" && (
          <div className="loading-row">
            <span className="spinner" />
            {t("search.searching")}
          </div>
        )}
        {status === "error" && (
          <div className="panel-message panel-error">{t("search.failed")}</div>
        )}
        {status === "empty" && (
          <div className="panel-message">{t("search.noResults")}</div>
        )}
        {status !== "loading" &&
          results.map((item) => {
            const label = getSearchLabel(item, fields, (id) =>
              t("common.feature", { id }),
            );

            return (
              <button
                className="search-result"
                type="button"
                key={item.id}
                title={label}
                onClick={() => zoomToSearchItem(ngwMap, searchResourceId, item)}
              >
                <span>{label}</span>
                <small>#{item.id}</small>
              </button>
            );
          })}
      </div>
    </form>
  );
}
